/**
 * Every photo the services page shows, in one place so the S3 paths are easy
 * to audit. Names match the slot in the design, not the file's subject.
 */
const IMAGE_BASE =
  "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-services";

/**
 * Describes one photo: where it lives and which part of it has to stay in
 * frame. Most slots crop the photo (`object-cover`), so without a focus point
 * the browser keeps the middle and can cut off a face or the front of a car.
 *
 * `focusX` / `focusY` are percentages of the image itself:
 *   focusX -> 0 = left edge,  50 = middle (default), 100 = right edge
 *   focusY -> 0 = top edge,   50 = middle (default), 100 = bottom edge
 *
 * Phones crop much more of the sides than desktop does, so the horizontal
 * focus can be set separately below the `lg` breakpoint:
 *   serviceImage("hero.webp", 10, 90, { mobileFocusX: 35 })
 * Without `mobileFocusX`, mobile uses the same `focusX` as desktop.
 *
 * Tune them here — nothing else needs touching.
 */
const serviceImage = (
  fileName,
  focusX = 50,
  focusY = 50,
  { mobileFocusX = focusX } = {}
) => ({
  src: `${IMAGE_BASE}/${fileName}`,
  focusX,
  focusY,
  mobileFocusX,
});

/**
 * Turns an entry above into the inline style its `<img>` needs. Every image on
 * the page is rendered with this (together with IMAGE_FOCUS_CLASS) so the
 * focus point is actually applied.
 *
 * An inline style cannot change with screen width, so it only hands the two
 * positions over as CSS variables; IMAGE_FOCUS_CLASS picks which one to use.
 */
export const getImageFocusStyle = (image) => ({
  "--image-focus-mobile": `${image.mobileFocusX}% ${image.focusY}%`,
  "--image-focus-desktop": `${image.focusX}% ${image.focusY}%`,
});

/** Applies the mobile focus point, then the desktop one from `lg` up. */
export const IMAGE_FOCUS_CLASS =
  "tw:[object-position:var(--image-focus-mobile)] tw:lg:[object-position:var(--image-focus-desktop)]";

export const SERVICE_IMAGES = {
  /** Wide banner behind the page title, shared by both tabs. */
  pageBanner: serviceImage("banner.webp", 40, 60),

  chauffeur: {
    hero: serviceImage("chauffeur/chauffeur.webp", 50, 50, { mobileFocusX: 75 }),
    airportTransferPair: [
      serviceImage("chauffeur/airport-transfer-1.webp", 50, 50),
      serviceImage("chauffeur/airport-transfer-2.webp", 10, 90),
    ],
    meetAssist: serviceImage("chauffeur/meet-assist-service.webp", 10, 90),
    fleetLineup: serviceImage("chauffeur/vehicle-comfort-aminities.webp", 50, 50),
    businessTransferPair: [
      serviceImage("chauffeur/business-transfer-1.webp", 50, 50),
      serviceImage("chauffeur/business-transfer-2.webp", 50, 50),
    ],
    concierge: serviceImage("chauffeur/concierge-service.webp", 50, 50),
    privateTourPair: [
      serviceImage("chauffeur/private-tour-1.webp", 50, 50),
      serviceImage("chauffeur/private-tour-2.webp", 50, 50),
    ],
  },

  meetGreet: {
    hero: serviceImage("meet-assist/meet-assist.webp", 10, 90, { mobileFocusX: 55 }),
  },
};
